import { Badge } from '@ayble-health/dashboard-components';
import React from 'react';
import { PmStatus } from '../../api/types';

enum ConfirmationStatus {
  confirmed = 'Confirmed',
  unconfirmed = 'Unconfirmed',
}

// Union of two enums because PmStatus does not cover the confirmation state,
// which comes from another property within the meeting object.
type Status = PmStatus & ConfirmationStatus;

const STATUS_BG_COLOR_MAP: Record<Status, string> = {
  [ConfirmationStatus.confirmed]: '#5299A9',
  [ConfirmationStatus.unconfirmed]: '#BCC7D7',
  [PmStatus.cancelled]: '#FEB117',
  [PmStatus.lateCancelled]: '#F8623F',
  [PmStatus.noShow]: '#5D1505',
};

const STATUS_TXT_COLOR_MAP: Record<Status, string> = {
  [ConfirmationStatus.confirmed]: '#FFFFFF',
  [ConfirmationStatus.unconfirmed]: '#2D6670',
  [PmStatus.cancelled]: '#2D6670',
  [PmStatus.lateCancelled]: '#FFFFFF',
  [PmStatus.noShow]: '#FFFFFF',
};

type Props = {
  isConfirmed: boolean;
  pmStatus: PmStatus | undefined;
};

/**
 * Renders a meeting's confirmation / `PmStatus` as a colored pill.
 * `pmStatus` wins if both are passed; falls back to confirmed /
 * unconfirmed when no explicit status is set. Bg + text colors come
 * from `STATUS_BG_COLOR_MAP` / `STATUS_TXT_COLOR_MAP` above (inline
 * hex per design — not from the theme tokens).
 *
 * @example
 * <MeetingStatusBadge isConfirmed pmStatus={undefined} />
 *
 * @example
 * <MeetingStatusBadge isConfirmed pmStatus={PmStatus.lateCancelled} />
 */
export const MeetingStatusBadge = ({ isConfirmed, pmStatus }: Props) => {
  const meetingStatus =
    pmStatus ??
    (isConfirmed
      ? ConfirmationStatus.confirmed
      : ConfirmationStatus.unconfirmed);

  return (
    <Badge
      backgroundColor={STATUS_BG_COLOR_MAP[meetingStatus]}
      rounded={'xl'}
      h={'20px'}
      mr={1}
      color={STATUS_TXT_COLOR_MAP[meetingStatus]} fontSize={'xs'}
    >
      {meetingStatus}
    </Badge>
  );
};
